import { api } from '../api'
import { zahlLesen } from '../format'
import { mvnAusGewicht, ohneMvn } from '../hilfen'
import { useAktion, useLaden } from '../hooks'
import Meldung from './Meldung'
import type { ArtikelEingabe, Artikelart, FehlenderArtikel } from '../typen'

/**
 * Die Artikel, die ein Import nennt, die es aber noch nicht in den Stammdaten gibt.
 *
 * Das ist der häufigste Grund fuer einen gesperrten Import - und war bisher eine Sackgasse: der
 * Befund sagte "71 Artikelnummer(n) ohne Stammsatz", und im Dashboard gab es keine Stelle, an der
 * sich ein Artikel anlegen ließ. Hier stehen sie mit dem Vorschlag des Backends, änderbar, und
 * danach wird derselbe Lauf erneut geprüft.
 *
 * Die MVN entscheidet der Mensch. Vorgeschlagen wird sie nur, wenn das Gebindegewicht in der
 * Bezeichnung steht.
 *
 * Ohne `laufId` zeigt sie den Stammdatenabgleich mit SelectLine: Artikel, die dort Bestand haben
 * und hier keinen Stammsatz - gemeldet, bevor ein Bestandslauf an ihnen scheitert.
 */
export default function FehlendeArtikel({
  laufId,
  onFertig,
}: {
  laufId?: number
  onFertig: () => void
}) {
  const ausSelectLine = laufId === undefined
  const { daten, setDaten: setZeilen, laedt, fehler: ladeFehler } = useLaden(
    async () => (laufId === undefined ? (await api.stammdatenAbgleich()).fehlend : api.fehlendeArtikel(laufId)),
    [laufId],
  )
  const { fehler, arbeitet, ausfuehren } = useAktion()
  const zeilen = daten ?? []

  function aendern(nummer: string, felder: Partial<FehlenderArtikel>) {
    setZeilen((alt) =>
      (alt ?? []).map((zeile) => (zeile.artikelnummer === nummer ? { ...zeile, ...felder } : zeile)),
    )
  }

  /** Aendert jemand das Gebindegewicht, folgt die MVN daraus - 1.000 kg geteilt durch das Gewicht. */
  function gewichtAendern(nummer: string, wert: string) {
    aendern(nummer, mvnAusGewicht(zahlLesen(wert)))
  }

  function zahlAendern(nummer: string, feld: 'mvn', wert: string) {
    aendern(nummer, { [feld]: zahlLesen(wert) })
  }

  function anlegen() {
    void ausfuehren(async () => {
      const eingaben: ArtikelEingabe[] = zeilen.map((zeile) => ({
        artikelnummer: zeile.artikelnummer,
        bezeichnung: zeile.bezeichnung,
        art: zeile.art,
        gebindegewicht_kg: zeile.gebindegewicht_kg,
        mvn: zeile.mvn,
        einheit: zeile.einheit,
        mvn_begruendung: zeile.mvn_begruendung,
        gebindegruppe: zeile.gebindegruppe,
        hersteller: null,
        lieferzeit_tage: null,
        mindestbestand_t: null,
        plan_t_je_monat: null,
        aktiv: zeile.aktiv,
      }))
      await api.artikelSammelAnlegen(eingaben)
      if (laufId !== undefined) await api.neuPruefen(laufId)
      onFertig()
    })
  }

  if (laedt || (daten === null && !ladeFehler)) return <p className="hinweis">Fehlende Artikel werden geladen.</p>
  if (ladeFehler) {
    return (
      <Meldung art="fehler" titel="Fehlende Artikel nicht geladen">
        {ladeFehler}
      </Meldung>
    )
  }
  if (zeilen.length === 0) return null

  const ohneUmrechnung = zeilen.filter(ohneMvn)

  return (
    <div className="karte" style={{ marginBottom: 16 }}>
      <h3>
        {zeilen.length} Artikel fehlen in den Stammdaten{ausSelectLine && ' - SelectLine führt sie im Bestand'}
      </h3>
      <p className="hinweis">
        Bezeichnung und Einheit stehen {ausSelectLine ? 'in SelectLine, der Hinweis sagt, in welchem Lager wie viel liegt' : 'im Dokument'}.
        Gebindegewicht und MVN sind Vorschlag, wo das Gewicht in der Bezeichnung steht - sonst leer.
        Rohstoffe gehören nicht in die Kladde, die Spalte disponieren bleibt bei ihnen leer.
        {ausSelectLine && ' Erst mit dem Klick wird angelegt - auch „nicht disponieren" ist eine Antwort, danach fragt niemand mehr.'}
      </p>

      {fehler && (
        <Meldung art="fehler" titel="Anlegen fehlgeschlagen">
          {fehler}
        </Meldung>
      )}

      <div className="tabellenrahmen">
        <table>
          <thead>
            <tr>
              <th>Artikel</th>
              <th>Bezeichnung</th>
              <th>Art</th>
              <th>Einheit</th>
              <th className="zahl">{ausSelectLine ? 'Läger' : 'Zeilen'}</th>
              <th className="zahl">Gebinde in kg</th>
              <th className="zahl">MVN je t</th>
              <th>Gruppe</th>
              <th>disponieren</th>
              <th>Hinweis</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((zeile) => (
              <tr key={zeile.artikelnummer}>
                <td>{zeile.artikelnummer}</td>
                <td>{zeile.bezeichnung}</td>
                <td>
                  <select
                    value={zeile.art}
                    onChange={(e) =>
                      aendern(zeile.artikelnummer, { art: e.target.value as Artikelart })
                    }
                  >
                    <option value="material">Material</option>
                    <option value="fertigartikel">Fertigartikel</option>
                  </select>
                </td>
                <td>{zeile.einheit}</td>
                <td className="zahl">{zeile.anzahl_positionen}</td>
                <td className="zahl">
                  <input
                    type="text"
                    style={{ width: 80, textAlign: 'right' }}
                    value={zeile.gebindegewicht_kg ?? ''}
                    disabled={zeile.art === 'fertigartikel'}
                    onChange={(e) => gewichtAendern(zeile.artikelnummer, e.target.value)}
                  />
                </td>
                <td className="zahl">
                  <input
                    type="text"
                    style={{ width: 90, textAlign: 'right' }}
                    className={ohneMvn(zeile) ? 'unter-mindestbestand' : ''}
                    value={zeile.mvn ?? ''}
                    disabled={zeile.art === 'fertigartikel'}
                    onChange={(e) => zahlAendern(zeile.artikelnummer, 'mvn', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    style={{ width: 120 }}
                    value={zeile.gebindegruppe ?? ''}
                    onChange={(e) =>
                      aendern(zeile.artikelnummer, { gebindegruppe: e.target.value || null })
                    }
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={zeile.aktiv}
                    onChange={(e) => aendern(zeile.artikelnummer, { aktiv: e.target.checked })}
                  />
                </td>
                <td className="hinweis">{zeile.hinweis ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ohneUmrechnung.length > 0 && (
        <Meldung art="warnung" titel="MVN fehlt">
          {ohneUmrechnung.length} disponierte Material-Artikel haben keine MVN. Ohne sie bleibt
          {ausSelectLine ? ' jeder Lauf mit ihnen' : ' der Import'} gesperrt - entweder MVN eintragen oder den Haken bei disponieren entfernen.
        </Meldung>
      )}

      <div className="leiste" style={{ marginTop: 12 }}>
        <button className="haupt" disabled={arbeitet} onClick={anlegen}>
          {ausSelectLine ? 'Artikel anlegen' : 'Artikel anlegen und erneut prüfen'}
        </button>
        <span className="hinweis">
          Vorhandene Artikelnummern werden übersprungen, der Vorgang ist wiederholbar.
        </span>
      </div>
    </div>
  )
}
